import { useEffect, useRef, useState } from 'react'
import { IconButton, Popover, Slider, Stack, Tooltip, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeDownIcon from '@mui/icons-material/VolumeDown'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import { useTranslation } from 'react-i18next'

interface VolumeControlProps {
  /** Current volume in [0, 1]. */
  volume: number
  /** Called with the new volume in [0, 1]. */
  onVolumeChange: (value: number) => void
  /** Size of the trigger icon button. */
  size?: 'small' | 'medium'
  /** Glyph size in px. Falls back to MUI's `fontSize="small"` when omitted. */
  iconFontSize?: number
  /** Extra sx for the trigger button — e.g. responsive `display` gating. */
  triggerSx?: SxProps<Theme>
}

/**
 * Reusable "nice" volume control: a single icon button (Up / Down / Off by
 * level) that pops a small slider above it. The icon in the popover toggles
 * mute, restoring the last non-zero level rather than jumping 0 → 1.
 *
 * Fully controlled — the parent owns the value and its persistence (the audio
 * engines already persist to localStorage `qlearn:volume`, so a level chosen on
 * one playback surface carries to the next).
 */
export function VolumeControl({
  volume,
  onVolumeChange,
  size = 'small',
  iconFontSize,
  triggerSx,
}: VolumeControlProps) {
  const { t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  // Remember the last audible level so muting is reversible.
  const lastNonZeroRef = useRef<number>(volume > 0 ? volume : 1)
  useEffect(() => {
    if (volume > 0) lastNonZeroRef.current = volume
  }, [volume])

  const toggleMute = () =>
    onVolumeChange(volume > 0 ? 0 : lastNonZeroRef.current || 1)

  const VolumeIcon = volume === 0 ? VolumeOffIcon : volume < 0.5 ? VolumeDownIcon : VolumeUpIcon
  const label = volume === 0 ? t('audio.muted') : t('audio.volume')
  const glyphSx = iconFontSize ? { fontSize: iconFontSize } : undefined

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size={size}
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t('audio.volume')}
          sx={{ flexShrink: 0, color: volume === 0 ? 'text.disabled' : 'text.secondary', ...triggerSx }}
        >
          <VolumeIcon fontSize={iconFontSize ? undefined : 'small'} sx={glyphSx} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 1.5, mb: 1 } } }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: { xs: 160, sm: 200 } }}>
          <Tooltip title={label}>
            <IconButton size="small" onClick={toggleMute} aria-label={t('audio.muted')}>
              <VolumeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Slider
            value={Math.round(volume * 100)}
            min={0}
            max={100}
            onChange={(_e, value) => onVolumeChange((value as number) / 100)}
            size="small"
            aria-label={t('audio.volume')}
            sx={{ flex: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: 'right' }}
          >
            {Math.round(volume * 100)}
          </Typography>
        </Stack>
      </Popover>
    </>
  )
}
